import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useState, useEffect, useCallback, useRef } from 'react';
import { getApi } from '../services/apiServices';
import { fetchCountriesEsim } from '../Utils/helper';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';
import { getDestinationCardImage } from '../Utils/destinationImages';
import { PinIcon, SearchIcon } from './r4l/icons';
import '../styles/r4l-home.css';

function Destination() {
  const location = useLocation();
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();
  const [countriesEsim, setCountriesEsim] = useState([]);
  const [filteredCountries, setFilteredCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState(() => {
    const urlParams = new URLSearchParams(location.search);
    return urlParams.get('search') || '';
  });
  const [retryCount, setRetryCount] = useState(0);
  // Arriving with ?search= (e.g. from the home page search) looks across all destinations, not just popular
  const [selectedFilter, setSelectedFilter] = useState(() => {
    const urlParams = new URLSearchParams(location.search);
    return urlParams.get('search') ? 'all' : 'popular';
  });
  const [totalCountries, setTotalCountries] = useState(0);
  const [selectedCurrency, setSelectedCurrency] = useState('USD');
  const [userChangedCurrency, setUserChangedCurrency] = useState(false);
  const searchTimeoutRef = useRef(null);
  const isInitialMount = useRef(true);
  
  // Initialize debouncedSearchQuery from URL params if they exist
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(() => {
    const urlParams = new URLSearchParams(location.search);
    return urlParams.get('search') || '';
  });

  // Language mapping function to convert app language codes to API language codes
  const getApiLanguageCode = (appLanguage) => {
    const languageMap = {
      'ar': 'ar',     // Arabic
      'da': 'da',     // Danish
      'de': 'de',     // German
      'en': 'en',     // English
      'es': 'es',     // Spanish
      'fr': 'fr',     // French
      'he': 'he',     // Hebrew
      'it': 'it',     // Italian
      'ja': 'ja',     // Japanese
      'ko': 'ko',     // Korean
      'nl': 'nl',     // Dutch
      'no': 'no',     // Norwegian
      'pl': 'pl',     // Polish
      'pt': 'pt',     // Portuguese
      'ru': 'ru',     // Russian
      'sv': 'sv',     // Swedish
      'tr': 'tr',     // Turkish
      'zh': 'zh_CN'   // Chinese (Simplified)
    };
    return languageMap[appLanguage] || 'en'; // Default to English if not found
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const searchParam = urlParams.get('search');
    if (searchParam) {
      setSearchQuery(searchParam);
      setSelectedFilter('all');
      // Clear any pending debounce timeout and set immediately for URL params
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      setDebouncedSearchQuery(searchParam);
    } else {
      // If URL param is removed, clear the search
      const currentSearch = searchQuery;
      if (currentSearch) {
        setSearchQuery('');
        if (searchTimeoutRef.current) {
          clearTimeout(searchTimeoutRef.current);
        }
        setDebouncedSearchQuery('');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);


  useEffect(() => {
    const storedCurrency = localStorage.getItem('selectedCurrency');
    if (storedCurrency) {
      setSelectedCurrency(storedCurrency);
    }
  }, []);


  useEffect(() => {
    const handleCurrencyChange = () => {
      const currency = localStorage.getItem('selectedCurrency') || 'USD';
      setSelectedCurrency(currency);
      setUserChangedCurrency(true); 
    };
    window.addEventListener('cartUpdated', handleCurrencyChange);
    return () => window.removeEventListener('cartUpdated', handleCurrencyChange);
  }, []);

  const fetchData = useCallback(async (filter = '', search = '') => {
    let apiFilter = '';
    if (filter === 'popular') {
      apiFilter = 'popular';
    } else if (filter === 'regional') {
      apiFilter = 'region';
    }    
    const searchParam = search.trim() || '';
    
    const currentCurrency = localStorage.getItem('selectedCurrency') || 'USD';
    const apiLanguage = getApiLanguageCode(currentLanguage);
    
    const shouldSendCurrency = currentCurrency !== 'USD';
    
    await fetchCountriesEsim(500, 1, setCountriesEsim, setError, setLoading, searchParam, apiFilter, setTotalCountries, currentCurrency, shouldSendCurrency, apiLanguage);
  }, [currentLanguage]);

  // Debounce search query (skip debouncing on initial mount if URL param exists)
  useEffect(() => {
    // Skip debouncing if this is the initial mount and we already have a value from URL
    if (isInitialMount.current) {
      const urlParams = new URLSearchParams(location.search);
      const hasInitialValue = urlParams.get('search');
      if (hasInitialValue) {
        isInitialMount.current = false;
        return;
      }
    }
    
    isInitialMount.current = false;
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500); // 500ms debounce delay

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery, location.search]);

  useEffect(() => {
    const loadData = async () => {
      await fetchData(selectedFilter, debouncedSearchQuery);
    };
    loadData();
  }, [selectedFilter, debouncedSearchQuery, selectedCurrency, fetchData]);

  useEffect(() => {
    setFilteredCountries(countriesEsim);
  }, [countriesEsim]);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleFilterChange = (filter) => {
    setSelectedFilter(filter);
    setSearchQuery(''); 
  };



  const filters = [
    ['popular', t('destination.filters.popular')],
    ['all', t('destination.filters.allDestinations')],
    ['regional', t('destination.filters.regionalPlans')]
  ];
  const fallbackFlag = window.getPublicUrl('images/earth-globe-global-svgrepo-com (1).svg');

  return (
    <div className="r4l-home">
      <section className="r4l-band r4l-list-page">
        <div className="r4l-wrap">
          <div className="r4l-list-head">
            <h1 className="r4l-block-title">{t('destination.title', { count: totalCountries || '' })}</h1>

            <div className="r4l-search r4l-list-search" role="search">
              <PinIcon />
              <input
                type="text"
                placeholder={t('destination.searchPlaceholder')}
                aria-label={t('destination.searchPlaceholder')}
                value={searchQuery}
                onChange={handleSearchChange}
              />
              <span className="r4l-search-go" aria-hidden="true"><SearchIcon /></span>
            </div>

            <div className="r4l-filter-pills" role="tablist">
              {filters.map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  aria-selected={selectedFilter === key}
                  onClick={() => handleFilterChange(key)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="r4l-dest-status r4l-list-loading"><span className="loader"></span></div>
          ) : (
            <div className="r4l-dest-grid r4l-list-grid">
              {filteredCountries.map((country, index) => {
                const photo = getDestinationCardImage(country?.iso3);
                const flag = country?.flag || fallbackFlag;
                return (
                  <article className="r4l-dest-card" key={country?.iso3 || index}>
                    <Link to={`/destination-detail/${country.iso3}`}>
                      <div className={`r4l-dest-photo${photo ? '' : ' is-flag'}`}>
                        <div className="r4l-dest-photo-clip">
                          <img
                            className="r4l-cover"
                            src={photo || flag.replace('/w80/', '/w320/')}
                            alt={`${country?.name} eSIM`}
                            loading="lazy"
                            onError={(e) => { e.target.src = fallbackFlag; }}
                          />
                        </div>
                        {photo && <img className="r4l-flag" src={flag} alt="" loading="lazy" />}
                      </div>
                      <div className="r4l-dest-meta">
                        <div>
                          <h3>{country?.name}</h3>
                          <p>{t('perGB')}: <b>{country?.symbol || '$'}{country?.lowest_rate || '49'}</b> {country?.currency || 'USD'}</p>
                        </div>
                        <span className="r4l-arrow" aria-hidden="true">→</span>
                      </div>
                    </Link>
                  </article>
                );
              })}
            </div>
          )}

          {!loading && !error && filteredCountries.length === 0 && (
            <div className="r4l-no-results">
              <h3>{t('destination.noResults.title')}</h3>
              <p>{t('destination.noResults.description')}</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

export default Destination;